// LocalStorage helpers for regular assessment attempt cache. These helpers stay
// deliberately narrow: Admission/KNET storage owns adm_* keys and is not touched.

const REGULAR_ASSESSMENT_SOURCE_FLOW = 'regular-assessment'

const safeSetItem = (key, value) => {
    if (value === undefined || value === null) return false

    try {
        if (typeof window === 'undefined' || !window.localStorage) return false

        window.localStorage.setItem(key, value)
        return true
    } catch (error) {
        console.warn(
            'localStorage write skipped (storage full?):',
            key,
            error?.name
        )
        return false
    }
}

const safeGetItem = key => {
    try {
        if (typeof window === 'undefined' || !window.localStorage) return null

        return window.localStorage.getItem(key)
    } catch {
        return null
    }
}

const getRegularAssessmentAttemptKeys = slug => [
    `attempt_id_${slug}`,
    `attempt_token_${slug}`,
]

const cacheRegularAssessmentAttemptStorage = (slug, attemptData) => {
    if (!slug || !attemptData?.id) return false

    // Remove stale values for this exact slug before storing the new attempt.
    // This avoids keeping an old token paired with a newer id if one write fails.
    clearRegularAssessmentAttemptStorage(slug)

    const [attemptIdKey, attemptTokenKey] = getRegularAssessmentAttemptKeys(slug)
    const didCacheId = safeSetItem(attemptIdKey, attemptData.id)
    const needsToken =
        attemptData.token !== undefined && attemptData.token !== null
    const didCacheToken = needsToken
        ? safeSetItem(attemptTokenKey, attemptData.token)
        : true

    const isFullyCached = didCacheId && didCacheToken
    if (!isFullyCached) clearRegularAssessmentAttemptStorage(slug)

    return isFullyCached
}

const getRegularAssessmentAttemptStorage = (slug, serverAttempt = {}) => {
    const [attemptIdKey, attemptTokenKey] = getRegularAssessmentAttemptKeys(slug)
    const cachedAttemptId = slug ? safeGetItem(attemptIdKey) : null
    const cachedToken = slug ? safeGetItem(attemptTokenKey) : null
    const serverAttemptId = serverAttempt?.id

    return {
        attemptId: serverAttemptId || cachedAttemptId,
        token:
            serverAttempt?.token ||
            (!serverAttemptId || serverAttemptId === cachedAttemptId
                ? cachedToken
                : undefined),
    }
}

const clearRegularAssessmentAttemptStorage = slug => {
    if (!slug || typeof window === 'undefined' || !window.localStorage) return

    for (const key of getRegularAssessmentAttemptKeys(slug)) {
        try {
            window.localStorage.removeItem(key)
        } catch {
            /* ignore */
        }
    }
}

const isAdmissionStorageContext = () => {
    const homePath = safeGetItem('home')
    const prevPage = safeGetItem('prevPage')

    return (
        homePath?.startsWith('/admission') ||
        prevPage?.includes('/admission') ||
        Boolean(safeGetItem('adm_token')) ||
        Boolean(safeGetItem('adm_home')) ||
        Boolean(safeGetItem('adm_exam_type')) ||
        Boolean(safeGetItem('adm_debug'))
    )
}

const shouldClearRegularAssessmentAttemptStorage = sourceFlow => {
    return (
        sourceFlow === REGULAR_ASSESSMENT_SOURCE_FLOW ||
        !isAdmissionStorageContext()
    )
}

export {
    cacheRegularAssessmentAttemptStorage,
    clearRegularAssessmentAttemptStorage,
    getRegularAssessmentAttemptStorage,
    isAdmissionStorageContext,
    REGULAR_ASSESSMENT_SOURCE_FLOW,
    shouldClearRegularAssessmentAttemptStorage,
}
